/**
 * Document History Manager
 * Manages a list of recently opened documents with localStorage persistence
 */

import { logger } from './logger';

// Type definitions
/** #98 part 4: a stored analysis of a Nextcloud file, reopened by fetching it again (no content is kept). */
export interface DocumentSource {
  kind: 'nextcloud';
  folderId: string;
  path: string;
}

export interface DocumentMetadata {
  name: string;
  size?: number;
  type?: string;
  lastModified?: Date | number;
  source?: DocumentSource;
}

export interface DocumentEntry {
  id: string;
  name: string;
  size: number;
  type: string;
  lastModified: number;
  openedAt: number;
  displayName: string;
  source?: DocumentSource;
}

/** Filename + lastModified; shared with processed-documents.ts (#96) so both name a file alike. */
export function documentIdOf(doc: DocumentMetadata): string {
  const timestamp = doc.lastModified
    ? new Date(doc.lastModified).getTime()
    : Date.now();
  return `${doc.name}-${timestamp}`;
}

class DocumentHistory {
  private maxDocuments: number;
  private storageKey: string;
  private documents: DocumentEntry[];

  /**
   * Create a new DocumentHistory instance
   * @param maxDocuments - Maximum number of documents to keep in history (default: 10)
   * @param storageKey - localStorage key for persisting history
   */
  constructor(
    maxDocuments: number = 10,
    storageKey: string = 'imsight-recent-documents'
  ) {
    this.maxDocuments = maxDocuments;
    this.storageKey = storageKey;
    this.documents = [];

    // Load existing history from localStorage
    this.loadFromStorage();
  }

  /**
   * Generate a unique ID for a document based on name and lastModified
   * @param doc - Document metadata
   * @returns Unique document ID
   */
  generateDocumentId(doc: DocumentMetadata): string {
    return documentIdOf(doc);
  }

  /**
   * Add a document to history (or move to top if already exists)
   * @param document - Document metadata object
   * @returns The document entry added to history
   */
  addDocument(document: DocumentMetadata): DocumentEntry | null {
    if (!document || !document.name) {
      logger.warn('Cannot add document to history: missing name');
      return null;
    }

    // Create document entry
    const docEntry: DocumentEntry = {
      id: this.generateDocumentId(document),
      name: document.name,
      size: document.size || 0,
      type: document.type || 'unknown',
      lastModified: document.lastModified
        ? new Date(document.lastModified).getTime()
        : Date.now(),
      openedAt: Date.now(),
      displayName: this.truncateFilename(document.name),
      ...(document.source ? { source: document.source } : {}),
    };

    // Check if document already exists in history
    const existingIndex = this.documents.findIndex(
      (doc) => doc.id === docEntry.id
    );

    if (existingIndex !== -1) {
      // Document exists - remove it so we can move it to the top
      this.documents.splice(existingIndex, 1);
    }

    // Add to beginning of array (most recent)
    this.documents.unshift(docEntry);

    // Trim to max documents
    if (this.documents.length > this.maxDocuments) {
      this.documents = this.documents.slice(0, this.maxDocuments);
    }

    // Persist to storage
    this.saveToStorage();

    return docEntry;
  }

  /**
   * Get all documents in history (most recent first)
   * @returns Array of document entries
   */
  getHistory(): DocumentEntry[] {
    return [...this.documents]; // Return a copy to prevent external modification
  }

  /**
   * Clear all documents from history
   */
  clearHistory(): void {
    this.documents = [];
    this.saveToStorage();
  }

  /**
   * Check if history is empty
   * @returns True if history is empty
   */
  isEmpty(): boolean {
    return this.documents.length === 0;
  }

  /**
   * Truncate long filenames for display
   * @param filename - Original filename
   * @param maxLength - Maximum length before truncation (default: 30)
   * @returns Truncated filename
   */
  truncateFilename(filename: string, maxLength: number = 30): string {
    if (!filename || filename.length <= maxLength) {
      return filename;
    }

    // Show beginning and end of filename
    const extensionMatch = filename.match(/\.[^.]+$/);
    const extension = extensionMatch ? extensionMatch[0] : '';
    const nameWithoutExt = extension
      ? filename.slice(0, -extension.length)
      : filename;

    if (nameWithoutExt.length + extension.length <= maxLength) {
      return filename;
    }

    const charsToShow = maxLength - extension.length - 3; // 3 for "..."
    const frontChars = Math.ceil(charsToShow * 0.6);
    const backChars = Math.floor(charsToShow * 0.4);

    return (
      nameWithoutExt.slice(0, frontChars) +
      '...' +
      nameWithoutExt.slice(-backChars) +
      extension
    );
  }

  /**
   * Save document history to localStorage
   * @returns True if save was successful
   */
  saveToStorage(): boolean {
    try {
      const data = JSON.stringify(this.documents);
      localStorage.setItem(this.storageKey, data);
      return true;
    } catch (error) {
      logger.warn('Unable to save document history to localStorage:', error);
      // Handle quota exceeded or other storage errors gracefully
      if ((error as Error).name === 'QuotaExceededError') {
        // Try to clear old entries and save again
        this.documents = this.documents.slice(
          0,
          Math.floor(this.maxDocuments / 2)
        );
        try {
          const data = JSON.stringify(this.documents);
          localStorage.setItem(this.storageKey, data);
          return true;
        } catch (retryError) {
          logger.error(
            'Failed to save document history after retry:',
            retryError
          );
        }
      }
      return false;
    }
  }

  /**
   * Load document history from localStorage
   * @returns True if load was successful
   */
  loadFromStorage(): boolean {
    try {
      const data = localStorage.getItem(this.storageKey);
      if (data) {
        const parsed = JSON.parse(data);
        if (Array.isArray(parsed)) {
          this.documents = parsed;
          // Ensure we don't exceed max documents
          if (this.documents.length > this.maxDocuments) {
            this.documents = this.documents.slice(0, this.maxDocuments);
            this.saveToStorage();
          }
          return true;
        }
      }
    } catch (error) {
      logger.warn('Unable to load document history from localStorage:', error);
      this.documents = [];
    }
    return false;
  }
}

// Export to window for debugging (dev only)
if (typeof window !== 'undefined' && import.meta.env.DEV) {
  window.DocumentHistory = DocumentHistory;
}

export default DocumentHistory;
